import { Spin } from "./Spin";
import {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { CloseCircleFilled, EyeInvisibleOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { useUiText } from "./uiText";
import { cx, useKitDisabled, useKitFieldStatus, useKitSize } from "./fieldContext";
import "../forms/browserAutofill.css";
import { KEY_ENTER } from "./keyboardKeys";
const DEFAULT_LINE_HEIGHT_PX = 22;

type FieldVariant = "outlined" | "filled" | "borderless" | "underlined";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> {
  size?: "small" | "middle" | "large";
  prefix?: ReactNode;
  suffix?: ReactNode;
  addonBefore?: ReactNode;
  addonAfter?: ReactNode;
  allowClear?: boolean | { clearIcon?: ReactNode };
  showCount?: boolean;
  status?: "error" | "warning" | "";
  /** Ohne Angabe folgt die Feldform dem Oberflächenstil (Noir: Linie, Kraftraum: Fläche). */
  variant?: FieldVariant;
  onPressEnter?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /** Hülle des Feldes (Antd: `rootClassName`). */
  rootClassName?: string;
}

type KitFocusOptions = FocusOptions & { cursor?: "start" | "end" | "all" };
/** Ref der Kit-Felder: das native Feld selbst; `input`/`nativeElement` und `focus({ cursor })` wie bei Antd. */
export type InputRef = Omit<HTMLInputElement, "focus"> & { input: HTMLInputElement | null; nativeElement: HTMLInputElement | null; focus: (options?: KitFocusOptions) => void };

/** Ergänzt das native Feld um die Antd-Ref-Schnittstelle (`focus({ cursor })`, `input`, `nativeElement`). */
function withKitRef<T extends HTMLInputElement | HTMLTextAreaElement, E extends object>(element: T | null, extra: E) {
  if (!element) return null as never;
  const focus = (options?: KitFocusOptions) => {
    HTMLElement.prototype.focus.call(element, options);
    const length = element.value.length;
    if (options?.cursor === "start") element.setSelectionRange(0, 0);
    else if (options?.cursor === "end") element.setSelectionRange(length, length);
    else if (options?.cursor === "all") element.select();
  };
  return Object.assign(element, { nativeElement: element, focus }, extra);
}

/** Setzt den Wert wie eine Nutzereingabe, damit `onChange` (auch in `Form.Item`) mit einem echten Ereignis läuft. */
function setNativeValue(element: HTMLInputElement | HTMLTextAreaElement, value: string) {
  const proto = element instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
  Object.getOwnPropertyDescriptor(proto, "value")?.set?.call(element, value);
  element.dispatchEvent(new Event("input", { bubbles: true }));
}

const valueLength = (value: unknown) => (value == null ? 0 : String(value).length);

/**
 * Einzeiliges Textfeld des Kits (Ersatz für Antd `Input`, gleiche Props). Funktioniert als Kind von
 * `Form.Item` (value/onChange/id) und zeigt dessen Validierungsstatus.
 */
export const InputBase = forwardRef<InputRef, InputProps>(function Input(
  { size, prefix, suffix, addonBefore, addonAfter, allowClear, showCount, status, variant, onPressEnter, onKeyDown, rootClassName, className, style, disabled, value, defaultValue, maxLength, ...rest },
  ref,
) {
  const { text } = useUiText();
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => withKitRef(innerRef.current, { input: innerRef.current }), []);
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const [uncontrolledLength, setUncontrolledLength] = useState(() => valueLength(defaultValue));
  const currentLength = value !== undefined ? valueLength(value) : uncontrolledLength;
  const showClear = Boolean(allowClear) && !mergedDisabled && !rest.readOnly && currentLength > 0;
  const hasAffix = prefix != null || suffix != null || allowClear || showCount;
  const fieldClass = cx(
    "ck-field",
    mergedSize === "small" && "ck-field--sm",
    mergedSize === "large" && "ck-field--lg",
    variant && `ck-field--${variant}`,
    mergedStatus && `is-${mergedStatus}`,
    mergedDisabled && "is-disabled",
  );

  const input = (
    <input
      ref={innerRef}
      className={cx(hasAffix ? "ck-field-input" : fieldClass, !hasAffix && className)}
      style={hasAffix || addonBefore != null || addonAfter != null ? undefined : style}
      disabled={mergedDisabled}
      value={value === null ? "" : value}
      defaultValue={defaultValue}
      maxLength={maxLength}
      aria-invalid={mergedStatus === "error" || undefined}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.key === KEY_ENTER) onPressEnter?.(event);
      }}
      {...rest}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        if (value === undefined) setUncontrolledLength(event.target.value.length);
        rest.onChange?.(event);
      }}
    />
  );

  const affixed = hasAffix ? (
    <span className={cx(fieldClass, "ck-field--affix", className)} style={addonBefore != null || addonAfter != null ? undefined : style}>
      {prefix != null ? <span className="ck-field-prefix">{prefix}</span> : null}
      {input}
      {allowClear ? (
        <button
          type="button"
          tabIndex={-1}
          className={cx("ck-field-clear", !showClear && "is-hidden")}
          aria-label={text("Clear")}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            if (!innerRef.current) return;
            setNativeValue(innerRef.current, "");
            return innerRef.current.focus();
          }}
        >
          {typeof allowClear === "object" && allowClear.clearIcon ? allowClear.clearIcon : <CloseCircleFilled />}
        </button>
      ) : null}
      {showCount ? <span className="ck-field-count">{maxLength ? `${currentLength} / ${maxLength}` : currentLength}</span> : null}
      {suffix != null ? <span className="ck-field-suffix">{suffix}</span> : null}
    </span>
  ) : input;

  if (addonBefore == null && addonAfter == null) {
    return rootClassName ? <span className={rootClassName}>{affixed}</span> : affixed;
  }
  return (
    <span className={cx("ck-field-group", rootClassName)} style={style}>
      {addonBefore != null ? <span className="ck-field-addon">{addonBefore}</span> : null}
      {affixed}
      {addonAfter != null ? <span className="ck-field-addon">{addonAfter}</span> : null}
    </span>
  );
});

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  autoSize?: boolean | { minRows?: number; maxRows?: number };
  showCount?: boolean;
  allowClear?: boolean;
  status?: "error" | "warning" | "";
  variant?: FieldVariant;
  size?: "small" | "middle" | "large";
  onPressEnter?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  style?: CSSProperties;
}

export type TextAreaRef = Omit<HTMLTextAreaElement, "focus"> & { nativeElement: HTMLTextAreaElement | null; resizableTextArea?: { textArea: HTMLTextAreaElement | null }; focus: (options?: KitFocusOptions) => void };

/** Mehrzeiliges Feld des Kits (Ersatz für Antd `Input.TextArea`), optional mitwachsend (`autoSize`). */
export const TextArea = forwardRef<TextAreaRef, TextAreaProps>(function TextArea(
  { autoSize, showCount, allowClear: _allowClear, status, variant, size, onPressEnter, onKeyDown, className, style, disabled, value, defaultValue, maxLength, rows, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => withKitRef(innerRef.current, { resizableTextArea: { textArea: innerRef.current } }), []);
  const mergedDisabled = useKitDisabled(disabled);
  const mergedSize = useKitSize(size);
  const mergedStatus = useKitFieldStatus(status);
  const [uncontrolledLength, setUncontrolledLength] = useState(() => valueLength(defaultValue));
  const currentLength = value !== undefined ? valueLength(value) : uncontrolledLength;
  const minRows = typeof autoSize === "object" ? autoSize.minRows : undefined;
  const maxRows = typeof autoSize === "object" ? autoSize.maxRows : undefined;

  // Höhe an den Inhalt anpassen (DOM-Messung, kein React-Zustand).
  useLayoutEffect(() => {
    const element = innerRef.current;
    if (!autoSize || !element) return;
    const lineHeight = Number.parseFloat(getComputedStyle(element).lineHeight) || DEFAULT_LINE_HEIGHT_PX;
    const chrome = element.offsetHeight - element.clientHeight;
    element.style.height = "auto";
    let next = element.scrollHeight + chrome;
    if (minRows) next = Math.max(next, minRows * lineHeight + chrome);
    if (maxRows) next = Math.min(next, maxRows * lineHeight + chrome);
    element.style.height = `${next}px`;
    element.style.overflowY = maxRows && element.scrollHeight + chrome > next ? "auto" : "hidden";
  });

  const area = (
    <textarea
      ref={innerRef}
      className={cx(
        "ck-field ck-field--area",
        mergedSize === "small" && "ck-field--sm",
        mergedSize === "large" && "ck-field--lg",
        variant && `ck-field--${variant}`,
        mergedStatus && `is-${mergedStatus}`,
        !showCount && className,
      )}
      style={showCount ? undefined : style}
      rows={autoSize ? minRows ?? rows ?? 1 : rows}
      disabled={mergedDisabled}
      value={value === null ? "" : value}
      defaultValue={defaultValue}
      maxLength={maxLength}
      aria-invalid={mergedStatus === "error" || undefined}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.key === KEY_ENTER) onPressEnter?.(event);
      }}
      {...rest}
      onChange={(event) => {
        if (value === undefined) setUncontrolledLength(event.target.value.length);
        rest.onChange?.(event);
      }}
    />
  );
  if (!showCount) return area;
  return (
    <span className={cx("ck-field-area-wrap", className)} style={style}>
      {area}
      <span className="ck-field-count">{maxLength ? `${currentLength} / ${maxLength}` : currentLength}</span>
    </span>
  );
});

export interface PasswordProps extends Omit<InputProps, "type"> {
  visibilityToggle?: boolean;
  visibilityLabels?: { show: string; hide: string };
}

/** Passwortfeld mit Anzeigen/Verbergen-Schalter (Ersatz für Antd `Input.Password`). */
export const Password = forwardRef<InputRef, PasswordProps>(function Password({ visibilityToggle = true, visibilityLabels, suffix, ...props }, ref) {
  const { text } = useUiText();
  const [visibility, setVisibility] = useState({ enabled: visibilityToggle, revealed: false });
  if (visibility.enabled !== visibilityToggle) setVisibility({ enabled: visibilityToggle, revealed: false });
  const visible = visibilityToggle && visibility.enabled && visibility.revealed;
  return (
    <InputBase
      ref={ref}
      {...props}
      type={visible ? "text" : "password"}
      suffix={
        <>
          {suffix}
          {visibilityToggle ? (
            <button type="button" className="ck-field-reveal" aria-label={visibilityLabels ? visible ? visibilityLabels.hide : visibilityLabels.show : text(visible ? "Hide password" : "Show password")} aria-pressed={visible} onClick={() => setVisibility({ enabled: visibilityToggle, revealed: !visible })}>
              {visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
            </button>
          ) : null}
        </>
      }
    />
  );
});

export interface SearchProps extends InputProps {
  onSearch?: (value: string, event?: unknown, info?: { source?: "input" | "clear" }) => void;
  enterButton?: boolean | ReactNode;
  loading?: boolean;
}

/** Suchfeld des Kits (Ersatz für Antd `Input.Search`): Enter oder Lupe löst `onSearch` aus. */
export const Search = forwardRef<InputRef, SearchProps>(function Search({ onSearch, enterButton, loading, onPressEnter, prefix, onChange, ...props }, ref) {
  const { text } = useUiText();
  const innerRef = useRef<InputRef>(null);
  useImperativeHandle(ref, () => innerRef.current as InputRef, []);
  const fire = () => onSearch?.(innerRef.current?.nativeElement?.value ?? "", undefined, { source: "input" });
  return (
    <InputBase
      ref={innerRef}
      type="search"
      prefix={prefix ?? (enterButton ? undefined : <SearchOutlined />)}
      onPressEnter={(event) => {
        onPressEnter?.(event);
        fire();
      }}
      onChange={(event) => {
        onChange?.(event);
        if (event.target.value === "" && props.allowClear) onSearch?.("", event, { source: "clear" });
      }}
      addonAfter={
        enterButton ? (
          <button type="button" className="ck-field-search-button" aria-label={text("Search")} onClick={fire}>
            {loading ? <Spin inline /> : enterButton === true ? <SearchOutlined /> : enterButton}
          </button>
        ) : undefined
      }
      suffix={!enterButton && loading ? <Spin inline /> : props.suffix}
      {...props}
    />
  );
});

